import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
} from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { CdkMenuModule } from '@angular/cdk/menu';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import {
  animate,
  query,
  stagger,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { AuthService } from '../../auth/auth/data-access/auth.service';
import { SubIdSelectorService } from './data-access/sub-id-selector.service';
import {
  EntitlmentResponse,
  TeamMember,
} from '@shared-interfaces/shared-entitlements';
import { MatTooltipModule } from '@angular/material/tooltip';
import { getDate } from '@shared-utils/helper/helper-functions';
import { EntitlementSettingsComponent } from './ui/entitlement-settings/entitlement-settings.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Settings } from '@shared-interfaces/shared-settings';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';

import { environment } from '@environments/environment';

@Component({
  selector: 'shared-sub-id-selector',
  standalone: true,
  imports: [
    CommonModule,
    CdkMenuModule,
    MatCardModule,
    MatIconModule,
    MatTooltipModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    TranslateModule
  ],
  templateUrl: './sub-id-selector.component.html',
  styleUrl: './sub-id-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.Default,
  animations: [
    trigger('races', [
      transition('* => *', [
        query(
          ':enter',
          [
            style({ opacity: 0, transform: 'translateY(10px)' }),
            stagger(100, [
              animate(
                '400ms ease-in',
                style({
                  opacity: 1,
                  transform: 'translateY(0%)',
                }),
              ),
            ]),
          ],
          { optional: true },
        ),
      ]),
    ]),
    trigger('fadeSlideInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('500ms', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('500ms', style({ opacity: 0 }))]),
    ]),
  ],
})
export class SubIdSelectorComponent implements OnInit {
  protected authService = inject(AuthService);
  protected selectorService = inject(SubIdSelectorService);
  protected status = this.selectorService.status;

  private router = inject(Router);

  constructor(public dialog: MatDialog) {}

  ngOnInit(): void {
    this.selectorService.getEntitlementConfig$.next();
  }

  ngOnDestroy(): void {
    document.body.style.backgroundColor = '#fff';
  }

  protected openSettings(event: Event, entitlements: EntitlmentResponse): void {
    event.stopPropagation();
    let dialogRef = this.dialog.open(EntitlementSettingsComponent, {
      height: 'fit-content',
      width: 'max(380px, 600px)',
      data: {
        serviceNickname: entitlements.serviceNickname,
        subscriptionId: entitlements.subscriptionId,
      },
    });

    dialogRef.afterClosed().subscribe((settings: Settings | 'true') => {
      if (settings === 'true' || !settings) {
        // closed without saving
      } else {
        this.selectorService.updateEntitlements$.next(settings);
      }
    });
  }

  protected navigateWithSubId(subId: string): void {
    const queryParams: NavigationExtras | undefined = subId
      ? {
          queryParams: { subscription_id: subId },
        }
      : {};
    this.router.navigate([environment.homeUrl], queryParams);
  }

  protected findMemberByRole(
    team: TeamMember[],
    role: string,
  ): TeamMember | undefined {
    return team.find((member) => member.roles.includes(role));
  }

  protected getUserList(team: TeamMember[]): string {
    const formattedNames = team.map(
      (member) => `${member.firstName} ${member.lastName}`,
    );
    return formattedNames.join('\n');
  }

  protected getDate(): string {
    return getDate();
  }
}
